import useAuthContext from './hooks/useAuthContext';
import React from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { Header, ScrollToTop } from './components';
import { Copyright } from './elements';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import { ToastContainer } from 'react-toastify';
import Routes from './Routes';
import { DASHBOARD_PATH } from './models/dashboard';
import { hasPermission } from './utils/permissionUtils';
import { Permission } from './constants/permissions';
import 'react-toastify/dist/ReactToastify.css';

/**
 * Switching business unit remounts every route, so a farm-scoped page starts over against the new farm. The
 * admin farms overview on `/dashboard` reads the whole estate and does not change with the farm selected, so
 * it is left mounted: rebuilding it would refetch the same data and replay its entrance animations.
 */
const UnitScopedRoutes: React.FC = () => {
  const { state } = useAuthContext();
  const { pathname } = useLocation();

  const onFarmsOverview =
    pathname.replace(/\/+$/, '') === DASHBOARD_PATH && hasPermission(state.role, Permission.VIEW_FARMS_OVERVIEW);

  return <Routes key={onFarmsOverview ? state.role : `${state.selectedBusinessUnitId}-${state.role}`} />;
};

function App() {
  const { state } = useAuthContext();

  // The shell pins the page to the viewport and lets <main> scroll, so this is the element ScrollToTop
  // has to reset - the window never scrolls.
  const contentRef = React.useRef<HTMLElement>(null);

  return (
    <div className="flex h-screen overflow-hidden">
      <BrowserRouter>
        <ScrollToTop containerRef={contentRef} />

        {state.isLoggedIn && <Header />}
        <main className="flex grow flex-col overflow-auto" ref={contentRef}>
          {/* Holds the content clear of the fixed app bar: an empty Toolbar is exactly the bar's height. */}
          <Toolbar />
          {/* 1920px is v4's xl, which the screens were laid out against; v9's xl is 1536px. */}
          <Container maxWidth={false} sx={{ py: 4, maxWidth: 1920 }}>
            <UnitScopedRoutes />
          </Container>

          <div className="mt-auto pb-2">
            <Copyright />
          </div>
        </main>
      </BrowserRouter>

      <ToastContainer newestOnTop pauseOnFocusLoss />
    </div>
  );
}

export default App;
